import {v4} from 'uuid';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import BenchlingConfig from 'BenchlingConfig';
import * as EventConstants from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import {ACTION_REFRESH} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {
  OptionalTableValidationResponse,
  TableValidationError,
} from 'notebook/structured-tables/StructuredTableApiConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {StructuredTableToTableMetadata} from 'notebook/tables/TableConstants';
import {
  batchedSubmissionGenerator,
  handleValidationError,
  isBatchingRequired,
  performClientSideWriteAction,
} from 'notebook/util/PerformActionFunctions';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  TABLE_REVALIDATE_FINISHED_EVENT,
  TABLE_REVALIDATE_STARTED_EVENT,
} from 'performance/processNotebookEvents';
import {ProcessTaskStructuredTableMetadata} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';

import {
  StructuredTableKind,
  TableActionFunctionParameters,
  UnknownStructuredTable,
} from '../StructuredTableConstants';
import {shouldValidateStructuredTable} from '../util/shouldValidateStructuredTable';
import makeSimpleActionFunction from './makeSimpleActionFunction';
import {getRowAwareLoadingMessage, RowSubmissionRange} from './makeSubmitRowActionFunction';

const VALIDATING_TABLE_MESSAGE = 'Validating table...';

/**
 * Sends batched requests to the server to validate rows in the given selection or the entire table.
 * If the table contains validation errors, this will throw a TableValidationError.  This will
 * automatically dispatch an event to notify the UI of the validation errors, so no action is needed by the
 * caller.
 * If the table has no validation errors, this will return and you can continue.
 */
export async function checkStructuredTableValidationErrors(
  entryId: string,
  structuredTable: UnknownStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  dataModsHeader?: DataModificationsHeader
): Promise<void> {
  if (!shouldValidateStructuredTable(structuredTable)) {
    return;
  }
  LoadingScreen.show(VALIDATING_TABLE_MESSAGE);
  const profileEventId = v4();
  const profilerMetadata = {
    actionMetadata: {
      entry_id: entryId.toLowerCase(),
      num_rows: computedTable.length,
      selection_length: selection ? selection.end - selection.start : computedTable.length,
      process_task_table_location:
        structuredTable.kind === StructuredTableKind.PROCESS_TASK
          ? (
              (structuredTable.metadata as ProcessTaskStructuredTableMetadata).tableLocation ?? ''
            ).toLowerCase()
          : '',
    },
    distributionMetadata: {
      structured_table_kind: structuredTable.kind.toLowerCase(),
      document_type: APIUtil.apiIdToType(entryId).toLowerCase(),
    },
  };
  logProfilerTimingEvent({...TABLE_REVALIDATE_STARTED_EVENT, id: profileEventId});
  const batchSize = BenchlingConfig.STRUCTURED_TABLE_VALIDATION_BATCH_SIZE ?? null;
  try {
    if (isBatchingRequired(computedTable, batchSize, selection)) {
      let submittedRange: RowSubmissionRange = {
        start: selection ? selection.start : 0,
        end: selection ? selection.end : 0,
      };
      const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);
      const responseData: OptionalTableValidationResponse = {
        error: 'Table validation error',
        tableName: structuredTable.name,
        tableErrors: [],
      };
      for (const {range, evaluatedRowBatch} of batches) {
        LoadingScreen.set(getRowAwareLoadingMessage(range, 'Validating Rows', 'Validating Row'));
        try {
          await ActionFunctionDAO.validateStructuredTable(
            entryId,
            structuredTable,
            evaluatedRowBatch,
            actionFunctionContext,
            range,
            undefined,
            dataModsHeader
          );
        } catch (e) {
          if (e instanceof TableValidationError) {
            responseData.tableErrors.push(...e.validationResponse.tableErrors);
          } else {
            throw e;
          }
        }
        submittedRange = {...submittedRange, end: range.end};
      }
      if (responseData.tableErrors.length > 0) {
        throw new TableValidationError(responseData);
      }
    } else {
      const pertinentComputedTable = selection
        ? computedTable.slice(selection.start, selection.end)
        : computedTable;
      await ActionFunctionDAO.validateStructuredTable(
        entryId,
        structuredTable,
        pertinentComputedTable,
        actionFunctionContext,
        selection,
        undefined,
        dataModsHeader
      );
      // Any new errors will be thrown as an exception, so if this passes then we can just continue.
    }
  } finally {
    logProfilerTimingEvent({...TABLE_REVALIDATE_FINISHED_EVENT, id: profileEventId}, profilerMetadata);
    // Any errors will be thrown by the ActionFunctionDAO, so we don't need to check for errors here.
    LoadingScreen.hide();
  }
}

/**
 * Validates a structured table.
 *
 * This acquires a document lock and saves any pending changes before running validation.
 *
 * @param entryOrTemplateId The ID of the entry or template that the structured table is associated with
 * @param structuredTable The structured table to validate
 * @param computedTable The computed table associated with the sttructured table
 * @returns True if the table is valid, false otherwise.
 */
export const invokeStructureTableValidation = async ({
  entryOrTemplateId,
  structuredTable,
  computedTable,
}: Pick<
  TableActionFunctionParameters<unknown, unknown>,
  'entryOrTemplateId' | 'structuredTable' | 'computedTable'
>): Promise<boolean> => {
  assert(entryOrTemplateId != null, 'entryOrTemplateId must be defined');
  try {
    await performClientSideWriteAction({
      entryOrTemplateId,
      loadingScreenMessage: 'Validating table...',
      autoSaverAction: ACTION_REFRESH,
      doPerformAction: async () => {
        await checkStructuredTableValidationErrors(entryOrTemplateId, structuredTable, computedTable, {
          entryId: undefined,
          procedureId: undefined,
          procedureMethodDefinitionVersionId: undefined,
          procedureStepExecutionId: undefined,
          requestV2SubmissionId: undefined,
        });
      },
      analyticsEvent: null,
    });
    // If there are no validation errors, then dispatch an event to clear any existing errors
    AppDispatcher.dispatch({
      actionType: EventConstants.STRUCTURED_TABLE_VALIDATION_ERRORS,
      entryId: entryOrTemplateId,
      validationErrorResponse: {
        error: 'Table validation error',
        tableName: structuredTable.name,
        tableErrors: [],
      },
    });
    // eslint-disable-next-line  benchlint/always-throw-from-catch
  } catch (e) {
    handleValidationError(e, entryOrTemplateId);
    return false;
  }
  return true;
};

export const validateStructuredTableActionFunction = makeSimpleActionFunction<{}, {}>({
  label: 'Validate table',
  icon: 'validation',
  iconAltText: 'Validate table',
  onInvoke: invokeStructureTableValidation,
  getHidden: ({structuredTable}) => !shouldValidateStructuredTable(structuredTable),
  getDisabled: ({table}) => (table?.metadata as StructuredTableToTableMetadata<unknown>)?.isConfig,
});
